import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Users, 
  Database, 
  Shield, 
  Lock, 
  LogOut, 
  Menu, 
  X, 
  ChevronRight, 
  ChevronLeft,
  PanelLeftClose,
  PanelLeft,
  MessageCircle, 
  Sparkles,
  Stethoscope,
  ExternalLink,
  ChevronDown,
  Search
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { adminAPI, settingsAPI } from '../services/api';
import CommandPalette from './CommandPalette';

export default function DashboardLayout({ user, onLogout }) {
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('nutrimus_sidebar_collapsed') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [whatsappSettings, setWhatsappSettings] = useState({ whatsapp_number: '', whatsapp_message: '' });
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = user?.role || (() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.role;
      }
    } catch (e) {}
    return null;
  })();

  const isTrial = userRole === 'trial';

  const toggleSidebar = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('nutrimus_sidebar_collapsed', String(next));
      } catch (e) {}
      return next;
    });
  };

  useEffect(() => {
    // Fetch public WhatsApp settings for user support
    settingsAPI.getPublicSettings()
      .then(res => setWhatsappSettings(res))
      .catch(() => {
        // Fallback if public endpoint not available
        settingsAPI.getSettings()
          .then(res => setWhatsappSettings(res))
          .catch(err => console.error('Failed to get WA settings:', err));
      });
  }, [user]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/patients') {
      return location.pathname === '/patients' || location.pathname.startsWith('/patients/');
    }
    return location.pathname === path;
  };

  const getPageMetadata = () => {
    const path = location.pathname;
    if (path === '/patients') {
      return { title: 'Manajemen Pasien', breadcrumb: 'Daftar Pasien' };
    }
    if (path.includes('/calculate')) {
      return { title: 'Kalkulasi Recall Nutrisi', breadcrumb: 'Recall Pasien' };
    }
    if (path.includes('/history')) {
      return { title: 'Riwayat Recall Nutrisi', breadcrumb: 'Riwayat Recall' };
    }
    if (path === '/foods') {
      return { title: 'Database Makanan', breadcrumb: 'Oral & Enteral' };
    }
    if (path === '/parenteral') {
      return { title: 'Database Parenteral', breadcrumb: 'Cairan & Elektrolit' };
    }
    if (path === '/admin') {
      return { title: 'Admin Dashboard', breadcrumb: 'Portal Admin' };
    }
    if (path === '/change-password') {
      return { title: 'Ubah Password', breadcrumb: 'Keamanan Akun' };
    }
    return { title: 'NUTRIMUS Portal', breadcrumb: 'Beranda' };
  };

  const { title, breadcrumb } = getPageMetadata();

  const handleOpenWhatsApp = () => {
    if (!whatsappSettings.whatsapp_number) return;
    const cleanNumber = whatsappSettings.whatsapp_number.replace(/\D/g, '');
    const encodedMsg = encodeURIComponent(whatsappSettings.whatsapp_message || 'Halo Admin NUTRIMUS, saya butuh bantuan...');
    window.open(`https://wa.me/${cleanNumber}?text=${encodedMsg}`, '_blank');
  };

  const navItems = [
    {
      label: 'Manajemen Pasien',
      description: 'Data pasien & input recall',
      path: '/patients',
      icon: Users,
      badge: null
    },
    {
      label: 'Database Makanan',
      description: 'Makanan oral & enteral',
      path: '/foods',
      icon: Database,
      badge: null
    },
    {
      label: 'Database Parenteral',
      description: 'Nutrisi parenteral & infus',
      path: '/parenteral',
      icon: Database,
      badge: null
    }
  ];

  if (user?.role === 'admin') {
    navItems.unshift({
      label: 'Admin Dashboard',
      description: 'Kelola user, kuota & audit',
      path: '/admin',
      icon: Shield
    });
  }

  const getRoleBadge = () => {
    if (user?.role === 'admin') {
      return (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
          ADMIN
        </span>
      );
    }
    if (user?.role === 'premium') {
      return (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          PREMIUM
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/30">
        TRIAL
      </span>
    );
  };

  const renderSidebar = (collapsed = false) => (
    <div className="h-full flex flex-col justify-between bg-slate-900 text-slate-200 select-none overflow-y-auto scrollbar-none">
      {/* Brand & User Card Header */}
      <div>
        {/* Brand Logo */}
        <div className={`border-b border-slate-800 flex items-center ${collapsed ? 'p-3.5 justify-center' : 'p-5 justify-between'}`}>
          <div 
            onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/patients')} 
            className={`flex items-center cursor-pointer group ${collapsed ? 'justify-center' : 'gap-3'}`}
            title="NUTRIMUS - Clinical Nutrition Recall"
          >
            <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center shadow-lg shadow-emerald-500/20 text-white font-bold group-hover:scale-105 transition-transform flex-shrink-0">
              <Stethoscope className="h-5 w-5" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-base text-white tracking-tight">NUTRIMUS</span>
                  {getRoleBadge()}
                </div>
                <p className="text-[11px] text-slate-400">Clinical Nutrition Recall</p>
              </div>
            )}
          </div>

          {/* Close button on mobile */}
          {!collapsed && (
            <button
              onClick={() => setMobileDrawerOpen(false)}
              className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* User Profile Snippet in Sidebar */}
        {collapsed ? (
          <div 
            className="p-2.5 mx-2.5 mt-3 rounded-xl bg-slate-800/60 border border-slate-800 flex justify-center cursor-pointer hover:bg-slate-800 transition-colors"
            title={`${user?.name || 'Ahli Gizi'} (${user?.role || 'trial'})`}
            onClick={() => navigate('/change-password')}
          >
            <div className="relative">
              <div className="h-9 w-9 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-bold text-xs flex-shrink-0">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-slate-900 ${
                user?.role === 'admin' ? 'bg-purple-500' : user?.role === 'premium' ? 'bg-emerald-500' : 'bg-sky-500'
              }`} />
            </div>
          </div>
        ) : (
          <div className="p-3.5 mx-3.5 mt-3.5 rounded-xl bg-slate-800/60 border border-slate-800">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-bold text-xs flex-shrink-0">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white truncate">{user?.name || 'Ahli Gizi'}</p>
                <p className="text-[11px] text-slate-400 truncate">{user?.email || ''}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Navigation Links */}
        <div className={`space-y-1.5 ${collapsed ? 'p-2 mt-2' : 'p-3.5'}`}>
          {!collapsed && (
            <p className="px-3 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Menu Aplikasi
            </p>
          )}

          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={collapsed ? `${item.label} (${item.description})` : undefined}
                className={`w-full flex items-center rounded-xl font-medium transition-all group relative ${
                  collapsed ? 'justify-center p-3' : 'justify-between px-3.5 py-2.5 text-left'
                } ${
                  active
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/70'
                }`}
              >
                <div className={`flex items-center min-w-0 ${collapsed ? 'justify-center' : 'gap-3.5'}`}>
                  <Icon className={`${collapsed ? 'h-5 w-5' : 'h-[18px] w-[18px]'} flex-shrink-0 transition-colors ${
                    active ? 'text-white' : 'text-slate-400 group-hover:text-emerald-400'
                  }`} />
                  {!collapsed && (
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-semibold leading-snug truncate">{item.label}</p>
                      <p className={`text-[11px] mt-0.5 truncate ${active ? 'text-emerald-100/90' : 'text-slate-400'}`}>
                        {item.description}
                      </p>
                    </div>
                  )}
                </div>

                {item.badge !== null && (
                  collapsed ? (
                    <span className="absolute top-2 right-2.5 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-slate-900 animate-pulse" />
                  ) : (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      active ? 'bg-white text-emerald-800' : item.badgeColor || 'bg-slate-800 text-slate-200'
                    }`}>
                      {item.badge}
                    </span>
                  )
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sidebar Footer: WhatsApp Support (Trial only), Password, Logout, and Collapse Toggle */}
      <div className={`border-t border-slate-800 bg-slate-950/40 space-y-2 ${collapsed ? 'p-2' : 'p-3.5'}`}>
        {whatsappSettings.whatsapp_number && isTrial && (
          collapsed ? (
            <button
              onClick={handleOpenWhatsApp}
              title="Bantuan WhatsApp"
              className="w-full flex items-center justify-center p-2.5 rounded-xl text-emerald-400 bg-emerald-950/30 hover:bg-emerald-900/40 border border-emerald-800/50 transition-colors group"
            >
              <MessageCircle className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleOpenWhatsApp}
              className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-emerald-400 bg-emerald-950/30 hover:bg-emerald-900/40 border border-emerald-800/50 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-emerald-400" />
                <span>Bantuan WhatsApp</span>
              </div>
              <ExternalLink className="h-3 w-3 text-emerald-500 opacity-60 group-hover:opacity-100" />
            </button>
          )
        )}

        {collapsed ? (
          <div className="space-y-1.5 pt-1">
            <button
              onClick={() => navigate('/change-password')}
              title="Ubah Password"
              className="w-full flex items-center justify-center p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800 transition-colors"
            >
              <Lock className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onLogout}
              title="Keluar"
              className="w-full flex items-center justify-center p-2 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-950/30 border border-red-900/40 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={toggleSidebar}
              title="Perluas Menu Sidebar"
              className="hidden lg:flex w-full items-center justify-center p-2 rounded-xl text-slate-400 hover:text-emerald-400 hover:bg-slate-800/80 border border-slate-800 transition-colors mt-2"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                onClick={() => navigate('/change-password')}
                className="flex-1 flex items-center justify-center gap-1.5 px-2 sm:px-2.5 py-2 rounded-xl text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800 transition-colors"
              >
                <Lock className="h-3.5 w-3.5" />
                <span>Ubah Password</span>
              </button>

              <button
                onClick={onLogout}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-950/30 border border-red-900/40 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Keluar</span>
              </button>
            </div>

            {/* Collapse toggle button at bottom of sidebar on desktop */}
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex w-full items-center justify-center gap-1.5 mt-2 py-1.5 rounded-lg text-[11px] text-slate-500 hover:text-slate-300 hover:bg-slate-800/60 transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              <span>Kecilkan Menu</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Fixed Sidebar (w-64 or w-20 when collapsed) */}
      <aside className={`hidden lg:flex flex-col h-screen fixed inset-y-0 left-0 border-r border-slate-800 z-40 shadow-xl transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}>
        {renderSidebar(isCollapsed)}
      </aside>

      {/* Mobile Slide-over Drawer (always full width) */}
      {mobileDrawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in"
            onClick={() => setMobileDrawerOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full h-full shadow-2xl z-10 animate-in slide-in-from-left duration-200">
            {renderSidebar(false)}
          </div>
        </div>
      )}

      {/* Main Content Area (dynamic padding: lg:pl-20 or lg:pl-64) */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out ${
        isCollapsed ? 'lg:pl-20' : 'lg:pl-64'
      }`}>
        {/* Modern Sticky Top Header */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xs border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile drawer toggle */}
            <button
              onClick={() => setMobileDrawerOpen(true)}
              className="lg:hidden p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              title="Buka Navigasi"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Desktop sidebar toggle button */}
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              title={isCollapsed ? "Perluas Sidebar" : "Kecilkan Sidebar"}
            >
              <PanelLeft className={`h-4 w-4 transition-colors ${isCollapsed ? 'text-emerald-600' : 'text-slate-600'}`} />
            </button>

            <div>
              <div className="flex items-center text-xs text-slate-400 gap-1.5">
                <span>NUTRIMUS</span>
                <ChevronRight className="h-3 w-3" />
                <span className="font-semibold text-slate-700">{breadcrumb}</span>
              </div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                {title}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Global Spotlight Quick Search Trigger */}
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200/80 border border-slate-200/80 text-slate-500 hover:text-slate-800 transition-all text-xs font-medium shadow-2xs group"
              title="Cari Cepat Pasien, Makanan, Menu (Shortcut: ⌘K atau Ctrl+K)"
            >
              <Search className="h-3.5 w-3.5 text-slate-400 group-hover:text-emerald-600 transition-colors" />
              <span className="hidden md:inline text-slate-600">Cari Cepat...</span>
              <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-300 shadow-2xs">
                ⌘K
              </kbd>
            </button>

            {whatsappSettings.whatsapp_number && isTrial && (
              <button
                onClick={handleOpenWhatsApp}
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                title="Bantuan WhatsApp"
              >
                <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                <span>Bantuan WA</span>
              </button>
            )}

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1.5 sm:px-3 sm:py-1.5 rounded-xl hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200">
                  <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-xs font-semibold text-slate-800 leading-none">{user?.name || 'Ahli Gizi'}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{user?.role || 'trial'}</p>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white shadow-lg border border-slate-200 p-1.5 rounded-xl">
                <div className="px-3 py-2 border-b border-slate-100 mb-1">
                  <p className="text-xs font-semibold text-slate-900">{user?.name || 'User'}</p>
                  <p className="text-[11px] text-slate-500">{user?.email}</p>
                </div>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="text-xs py-2 cursor-pointer font-semibold text-purple-700">
                    <Shield className="h-3.5 w-3.5 mr-2 text-purple-600" />
                    Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/patients')} className="text-xs py-2 cursor-pointer">
                  <Users className="h-3.5 w-3.5 mr-2 text-slate-500" />
                  Manajemen Pasien
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/foods')} className="text-xs py-2 cursor-pointer">
                  <Database className="h-3.5 w-3.5 mr-2 text-slate-500" />
                  Database Makanan
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/change-password')} className="text-xs py-2 cursor-pointer">
                  <Lock className="h-3.5 w-3.5 mr-2 text-slate-500" />
                  Ubah Password
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuItem onClick={onLogout} className="text-xs py-2 text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer font-medium">
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content Body */}
        <main className="flex-1">
          <Outlet />
        </main>
      </div>

      {/* Global Command Palette / Spotlight (Cmd+K) */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        user={user}
      />
    </div>
  );
}
